import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { Can } from '../../shell/Can.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import {
  listRoles,
  getUserRoles,
  assignUserRole,
  revokeUserRole,
  type Role,
  type UserRoleMap,
} from '../../api/rolesApi.js';

interface UserRolesPanelProps {
  readonly userId: string;
}

/**
 * User↔Role assignment panel (M02-G03). Lists the user's active role
 * assignments with a revoke control and a picker to assign further roles.
 * Mutating controls are gated by the menu-driven <Can> guard; the backend
 * independently enforces the ASSIGN/ROLES permission.
 */
export function UserRolesPanel({ userId }: UserRolesPanelProps): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { day } = useBusinessDay();
  const { token } = useAuth();
  const [roles, setRoles] = useState<readonly Role[] | null>(null);
  const [assigned, setAssigned] = useState<readonly UserRoleMap[] | null>(null);
  const [selected, setSelected] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([listRoles(token), getUserRoles(userId, token)])
      .then(([r, a]) => {
        if (!alive) return;
        setRoles(r);
        setAssigned(a);
      })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [userId, token]);

  const roleName = useMemo(() => {
    const map = new Map<string, Role>();
    (roles ?? []).forEach((r) => map.set(r.roleId, r));
    return (roleId: string) => map.get(roleId)?.roleName ?? roleId;
  }, [roles]);

  // The licence a role consumes has to be visible against the user who holds it.
  const roleCategory = useMemo(() => {
    const map = new Map<string, Role>();
    (roles ?? []).forEach((r) => map.set(r.roleId, r));
    return (roleId: string) => {
      const code = map.get(roleId)?.roleCategoryCode;
      return code ? t(`roles.category.${code}`) : '-';
    };
  }, [roles, t]);

  /** Roles the user does not already hold — the assignable set. */
  const assignable = useMemo(() => {
    if (!roles || !assigned) return [];
    const held = new Set(assigned.map((a) => a.roleId));
    return roles.filter((r) => !held.has(r.roleId));
  }, [roles, assigned]);

  async function refreshAssigned() {
    setAssigned(await getUserRoles(userId, token));
  }

  async function onAssign() {
    if (!selected) return;
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      await assignUserRole(userId, selected, token);
      setSelected('');
      await refreshAssigned();
      setNotice(t('users.roles.assigned'));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function onRevoke(mapId: string) {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      await revokeUserRole(mapId, token);
      await refreshAssigned();
      setNotice(t('users.roles.revoked'));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-label="user-roles">
      <h2>{t('users.roles.title')}</h2>
      {error ? <p role="alert">{error}</p> : null}
      {notice ? <p role="status">{notice}</p> : null}
      {!assigned ? <p>{t('users.roles.loading')}</p> : assigned.length === 0 ? (
        <p>{t('users.roles.empty')}</p>
      ) : (
        <table aria-label="assigned-roles">
          <thead><tr><th>{t('users.roles.col.role')}</th><th>{t('users.roles.col.category')}</th><th>{t('users.roles.col.effectiveFrom')}</th><th /></tr></thead>
          <tbody>
            {assigned.map((a) => (
              <tr key={a.mapId}>
                <td>{roleName(a.roleId)}</td>
                <td aria-label={`role-category-${a.roleId}`}>{roleCategory(a.roleId)}</td>
                <td>{day(a.effectiveFromDate)}</td>
                <td>
                  <Can path="/admin/roles" action="edit">
                    <button type="button" onClick={() => onRevoke(a.mapId)} disabled={busy}>
                      {t('users.roles.revoke')}
                    </button>
                  </Can>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Can path="/admin/roles" action="edit">
        <div>
          <label htmlFor="role-select">{t('users.roles.assignRole')}</label>
          <select
            id="role-select"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            disabled={busy || assignable.length === 0}
          >
            <option value="">{t('users.roles.selectRole')}</option>
            {assignable.map((r) => (
              <option key={r.roleId} value={r.roleId}>{r.roleName}</option>
            ))}
          </select>
          <button type="button" onClick={onAssign} disabled={busy || !selected}>
            {t('users.roles.assign')}
          </button>
        </div>
      </Can>
    </section>
  );
}
